import type { AuthProvider } from "ra-core";
import { supabaseAuthProvider } from "ra-supabase-core";

import { canAccess } from "../commons/canAccess";
import { getSupabaseClient } from "./supabase";

// IDEM NOTE: this file was rewritten from Atomic CRM's `sales`-table auth to the
// Idem spine. The current user lives in `public.users` (uuid PK, `auth_id` FK to
// auth.users, `role_id` FK to `roles`) — there is no `sales` table. Admin is
// role_id = 1 (see 0001 seed / D5). Idem provisions users via admin (Supabase
// Admin API), so there is NO self-serve first-run "initialization" wizard — the
// old `init_state` funnel is removed.

const getBaseAuthProvider = () =>
  supabaseAuthProvider(getSupabaseClient(), {
    getIdentity: async () => {
      const user = await getCurrentUser();

      if (user == null) {
        throw new Error();
      }

      return {
        id: user.id,
        fullName: user.full_name ?? user.email,
        avatar: undefined,
      };
    },
  });

// Cache the current user in local storage; cleared on logout.
const CURRENT_USER_CACHE_KEY = "RaStore.auth.current_user";

function getLocalStorage(): Storage | null {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  return null;
}

// Idem has no first-run setup wizard — admin creates users out of band. Always
// initialized. Kept as an async function so the call sites are unchanged.
export async function getIsInitialized() {
  return true;
}

type CurrentUser = {
  id: string;
  full_name: string | null;
  email: string;
  role_id: number;
};

const getCurrentUser = async (): Promise<CurrentUser | undefined> => {
  const storage = getLocalStorage();
  const cachedValue = storage?.getItem(CURRENT_USER_CACHE_KEY);
  if (cachedValue != null) {
    return JSON.parse(cachedValue);
  }

  const { data: dataSession, error: errorSession } =
    await getSupabaseClient().auth.getSession();

  if (dataSession?.session?.user == null || errorSession) {
    return undefined;
  }

  const { data: user, error } = await getSupabaseClient()
    .from("users")
    .select("id, full_name, email, role_id")
    .match({ auth_id: dataSession.session.user.id })
    .single();

  if (user == null || error) {
    return undefined;
  }

  storage?.setItem(CURRENT_USER_CACHE_KEY, JSON.stringify(user));
  return user as CurrentUser;
};

function clearCache() {
  const storage = getLocalStorage();
  storage?.removeItem(CURRENT_USER_CACHE_KEY);
}

export const getAuthProvider = (): AuthProvider => {
  const baseAuthProvider = getBaseAuthProvider();
  return {
    ...baseAuthProvider,
    login: async (params) => {
      if (params.ssoDomain) {
        const { error } = await getSupabaseClient().auth.signInWithSSO({
          domain: params.ssoDomain,
        });
        if (error) {
          throw error;
        }
        return;
      }
      return baseAuthProvider.login(params);
    },
    logout: async (params) => {
      clearCache();
      return baseAuthProvider.logout(params);
    },
    checkAuth: async (params) => {
      // Password-flow pages: nothing to do.
      if (
        window.location.pathname === "/set-password" ||
        window.location.hash.includes("#/set-password") ||
        window.location.pathname === "/forgot-password" ||
        window.location.hash.includes("#/forgot-password")
      ) {
        return;
      }
      return baseAuthProvider.checkAuth(params);
    },
    canAccess: async (params) => {
      const user = await getCurrentUser();
      if (user == null) return false;

      // Stage 0 simplified RBAC (D14): admin (role_id=1) => all; everyone else
      // => everything except the sales/configuration resources. Real per-role
      // rules arrive with the permission matrix.
      const role = user.role_id === 1 ? "admin" : "user";
      return canAccess(role, params);
    },
    getAuthorizationDetails(authorizationId: string) {
      return getSupabaseClient().auth.oauth.getAuthorizationDetails(
        authorizationId,
      );
    },
    approveAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.approveAuthorization(
        authorizationId,
      );
    },
    denyAuthorization(authorizationId: string) {
      return getSupabaseClient().auth.oauth.denyAuthorization(authorizationId);
    },
  };
};
